// DraggableRect — wiederverwendbare SVG-Rechteck-Komponente
// mit Drag, Resize (8 Handles), Selection, Pan/Zoom-aware.
//
// Geometrie: { x, y, width, height, rotation?, color? }
// Coords: SVG world-space (nach viewBox-Transform).
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

const HANDLE_SIZE = 8;
const MIN_SIZE = 10;

// Konvertiert Browser-Maus-Event → SVG world-space Koordinaten
export function clientToSvg(svgEl, clientX, clientY) {
  if (!svgEl) return { x: 0, y: 0 };
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const inv = ctm.inverse();
  const out = pt.matrixTransform(inv);
  return { x: out.x, y: out.y };
}

const HANDLES = [
  { key: 'nw', cursor: 'nwse-resize', dx: 0,    dy: 0    },
  { key: 'n',  cursor: 'ns-resize',   dx: 0.5,  dy: 0    },
  { key: 'ne', cursor: 'nesw-resize', dx: 1,    dy: 0    },
  { key: 'e',  cursor: 'ew-resize',   dx: 1,    dy: 0.5  },
  { key: 'se', cursor: 'nwse-resize', dx: 1,    dy: 1    },
  { key: 's',  cursor: 'ns-resize',   dx: 0.5,  dy: 1    },
  { key: 'sw', cursor: 'nesw-resize', dx: 0,    dy: 1    },
  { key: 'w',  cursor: 'ew-resize',   dx: 0,    dy: 0.5  },
];

export function DraggableRect({
  svgRef,
  geometry,
  label,
  selected,
  canEdit,
  onSelect,
  onChange,           // (newGeometry) => void   — live während Drag
  onCommit,           // (newGeometry) => void   — beim Loslassen (zum Speichern)
  onDoubleClick,
  fillOverride,
  strokeOverride,
  className,
  testId,
  snapTargets,        // Array von {x,y,width,height} für Snap (andere Rechtecke)
  snapThreshold = 6,  // World-Units
}) {
  const [drag, setDrag] = useState(null);
  // local geometry = optimistic während Drag/Resize, sonst props.geometry
  const [local, setLocal] = useState(geometry);

  useEffect(() => {
    if (!drag) setLocal(geometry);
  }, [geometry, drag]);

  const startDrag = useCallback((e, mode) => {
    if (!canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const start = clientToSvg(svgRef.current, e.clientX, e.clientY);
    setDrag({
      mode,
      startX: start.x,
      startY: start.y,
      origGeo: { ...local },
    });
    onSelect?.();
  }, [canEdit, svgRef, local, onSelect]);

  useEffect(() => {
    if (!drag) return;
    // Sammle Snap-Linien aus anderen Rects (Edges + Mitten)
    const xLines = [];
    const yLines = [];
    (snapTargets || []).forEach((t) => {
      xLines.push(t.x, t.x + t.width, t.x + t.width / 2);
      yLines.push(t.y, t.y + t.height, t.y + t.height / 2);
    });
    const snap = (val, lines) => {
      let best = null;
      let bestDist = snapThreshold;
      lines.forEach((line) => {
        const d = Math.abs(val - line);
        if (d < bestDist) { bestDist = d; best = line; }
      });
      return best;
    };
    const handleMove = (e) => {
      const cur = clientToSvg(svgRef.current, e.clientX, e.clientY);
      const dx = cur.x - drag.startX;
      const dy = cur.y - drag.startY;
      const g = { ...drag.origGeo };
      if (drag.mode === 'move') {
        g.x = drag.origGeo.x + dx;
        g.y = drag.origGeo.y + dy;
        // Snap (außer Shift gedrückt = freihand)
        if (!e.shiftKey && xLines.length) {
          // Prüfe left, right, center-x
          const candidates = [
            { val: g.x, set: (v) => { g.x = v; } },
            { val: g.x + g.width, set: (v) => { g.x = v - g.width; } },
            { val: g.x + g.width / 2, set: (v) => { g.x = v - g.width / 2; } },
          ];
          for (const c of candidates) {
            const s = snap(c.val, xLines);
            if (s !== null) { c.set(s); break; }
          }
          const candY = [
            { val: g.y, set: (v) => { g.y = v; } },
            { val: g.y + g.height, set: (v) => { g.y = v - g.height; } },
            { val: g.y + g.height / 2, set: (v) => { g.y = v - g.height / 2; } },
          ];
          for (const c of candY) {
            const s = snap(c.val, yLines);
            if (s !== null) { c.set(s); break; }
          }
        }
      } else {
        // Resize per Handle
        let { x, y, width, height } = drag.origGeo;
        const h = drag.mode;
        if (h.includes('w')) { x = drag.origGeo.x + dx; width = drag.origGeo.width - dx; }
        if (h.includes('e')) { width = drag.origGeo.width + dx; }
        if (h.includes('n')) { y = drag.origGeo.y + dy; height = drag.origGeo.height - dy; }
        if (h.includes('s')) { height = drag.origGeo.height + dy; }
        // Mindestgröße
        if (width < MIN_SIZE) {
          if (h.includes('w')) x = drag.origGeo.x + drag.origGeo.width - MIN_SIZE;
          width = MIN_SIZE;
        }
        if (height < MIN_SIZE) {
          if (h.includes('n')) y = drag.origGeo.y + drag.origGeo.height - MIN_SIZE;
          height = MIN_SIZE;
        }
        g.x = x; g.y = y; g.width = width; g.height = height;
      }
      setLocal(g);
      onChange?.(g);
    };
    const handleUp = () => {
      setDrag(null);
      onCommit?.(local);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [drag, local, onChange, onCommit, svgRef, snapTargets, snapThreshold]);

  const g = local;
  const fill = fillOverride || g.color || '#e2e8f0';
  const stroke = strokeOverride || (selected ? '#dc2626' : '#475569');

  return (
    <g data-testid={testId} className={className}>
      <rect
        x={g.x} y={g.y} width={g.width} height={g.height}
        fill={fill}
        stroke={stroke}
        strokeWidth={selected ? 2 : 1}
        opacity={0.85}
        style={{ cursor: canEdit ? 'move' : 'pointer' }}
        onPointerDown={(e) => startDrag(e, 'move')}
        onClick={(e) => { e.stopPropagation(); onSelect?.(); }}
        onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick?.(); }}
      />
      {label && (
        <text
          x={g.x + g.width / 2}
          y={g.y + g.height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.min(g.width / 6, g.height / 4, 14)}
          fill="#1e293b"
          fontWeight="600"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {label}
        </text>
      )}
      {selected && canEdit && HANDLES.map((h) => {
        const cx = g.x + g.width * h.dx;
        const cy = g.y + g.height * h.dy;
        return (
          <rect
            key={h.key}
            x={cx - HANDLE_SIZE / 2}
            y={cy - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill="#fff"
            stroke="#dc2626"
            strokeWidth={1.5}
            style={{ cursor: h.cursor }}
            onPointerDown={(e) => startDrag(e, h.key)}
          />
        );
      })}
    </g>
  );
}

export default DraggableRect;
